import { createTheme } from '@mui/material/styles';

// Editorial dark — Resy/Infatuation-adjacent. Warm near-black (not stock
// slate), paper cream text, a single rust accent used sparingly for CTAs.
// Fraunces stays as a display face for restaurant names + screen titles only;
// Inter does all the UI work (replaced Manrope which read generic/templatey).

export const FONT_DISPLAY = '"Fraunces", "Playfair Display", "Georgia", serif';
export const FONT_BODY = '"Inter", "Helvetica Neue", "Arial", sans-serif';

export const COLORS = {
  bg: '#0b0a0c',
  surface: '#141214',
  surfaceElevated: '#1b181a',
  divider: 'rgba(244,239,233,0.06)',
  textPrimary: '#f4efe9',
  textSecondary: '#938a7f',
  textTertiary: '#5c554d',
  accent: '#e36950',       // rust / warmer than default orange
  accentSoft: '#f0a593',
  gold: '#d9b26b',
  sage: '#8fa98a',
  rose: '#c87f81',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: COLORS.accent, light: COLORS.accentSoft, dark: '#b8513c' },
    secondary: { main: COLORS.sage },
    background: { default: COLORS.bg, paper: COLORS.surface },
    text: {
      primary: COLORS.textPrimary,
      secondary: COLORS.textSecondary,
      disabled: COLORS.textTertiary,
    },
    divider: COLORS.divider,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT_BODY,
    // Editorial display sizes
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: '-0.01em' },
    // Tight editorial body sizes
    body1: { fontSize: '0.92rem', letterSpacing: '-0.005em' },
    body2: { fontSize: '0.82rem', letterSpacing: '-0.003em' },
    caption: { letterSpacing: 0 },
    button: { fontWeight: 600, letterSpacing: '-0.005em' },
    // Overline used as "section eyebrow"
    overline: {
      fontFamily: FONT_BODY,
      fontWeight: 600,
      fontSize: '0.66rem',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      lineHeight: 1.6,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: COLORS.bg,
          color: COLORS.textPrimary,
          // Prevent iOS blue tap highlight — it reads as cheap on dark UI
          WebkitTapHighlightColor: 'transparent',
        },
        '*::-webkit-scrollbar': { width: 6, height: 6 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(244,239,233,0.09)', borderRadius: 8 },
        '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(244,239,233,0.18)' },
        '*': { scrollbarWidth: 'thin', scrollbarColor: 'rgba(244,239,233,0.09) transparent' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', border: 'none' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, borderRadius: 999,
          letterSpacing: '-0.005em',
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontFamily: FONT_BODY, letterSpacing: 0 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          backgroundColor: COLORS.surface,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: COLORS.surfaceElevated,
          border: `1px solid ${COLORS.divider}`,
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surfaceElevated,
          color: COLORS.textPrimary,
          border: `1px solid ${COLORS.divider}`,
          borderRadius: 12,
          fontWeight: 500,
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: COLORS.surfaceElevated,
          border: `1px solid ${COLORS.divider}`,
          fontWeight: 500,
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: COLORS.divider } },
    },
  },
});

export default theme;
